// client/src/pages/Home.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings.js';
import api from '../services/api.js';

// ─── Toast ──────────────────────────────────────────────
// One-shot toast for messages stashed in sessionStorage by other pages
// (e.g. RedeemGift.js's "Save for Later"). Self-clears after ~4 seconds.
function Toast({ message, onDone }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onDone, 4000);
    return () => clearTimeout(t);
  }, [message, onDone]);

  if (!message) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 max-w-sm w-full pointer-events-none">
      <div className="bg-royal-900 dark:bg-royal-800 border border-gold/40 rounded-xl shadow-2xl px-5 py-3 flex items-center gap-3 pointer-events-auto">
        <span className="text-2xl shrink-0">🎁</span>
        <p className="text-white text-sm leading-snug">{message}</p>
      </div>
    </div>
  );
}

// ─── Animated tally number ──────────────────────────────
// Counts up to the target once it arrives. No library; rAF easeOutCubic.
function CountUp({ value, durationMs = 1400 }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!value || value <= 0) {
      setDisplay(value || 0);
      return;
    }
    let raf;
    const start = performance.now();
    const to = value;
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(to * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, durationMs]);

  return <>{display.toLocaleString()}</>;
}

// ─── Corner sparkle decals ──────────────────────────────
// The ✦ marks placed in all four corners of the tally box.
function CornerSparkles() {
  return (
    <>
      <span className="absolute top-3 left-4 text-gold opacity-30 text-base select-none">✦</span>
      <span className="absolute top-3 right-4 text-gold opacity-30 text-base select-none">✦</span>
      <span className="absolute bottom-3 left-4 text-gold opacity-30 text-base select-none">✦</span>
      <span className="absolute bottom-3 right-4 text-gold opacity-30 text-base select-none">✦</span>
    </>
  );
}

export default function Home() {
  const { settings } = useSettings();

  // Display-name settings (existing).
  const cardsName = settings.cards_display_name || 'Moonbeams';
  const storiesName = settings.stories_display_name || 'Stories';
  const siteName = settings.site_name || 'Fairytale';

  // Editable homepage text — every section reads its copy from settings with
  // a sensible default fallback. {cards} / {stories} placeholders in button
  // labels are substituted with the display-name settings above.
  const sub = (s) => (s || '').replace(/\{cards\}/g, cardsName).replace(/\{stories\}/g, storiesName);

  const heroWelcome = settings.home_hero_welcome || 'Welcome to';
  const heroBrowseBtn = sub(settings.home_hero_browse_button || 'Moonbeam eCards');
  const heroStoriesBtn = sub(settings.home_hero_stories_button || 'Read {stories}');

  const tallyHeading = settings.home_tally_heading || 'Moonbeams Shared';
  const tallyLabel = settings.home_tally_label || 'moonbeams created and sent';
  const communityText = settings.home_community_text || '';

  const storiesHeading = settings.home_stories_heading || 'Free Fairy Tales';
  const storiesIntro = settings.home_stories_intro
    || 'Enjoy our collection of traditional fairytales and present-day Cherry Fairy Chronicles';
  const storiesBtn1 = settings.home_stories_button_1 || 'Traditional Fairytales';
  const storiesBtn2 = settings.home_stories_button_2 || 'The Cherry Fairy Chronicles';

  const newsletterHeading = settings.home_newsletter_heading || 'Subscribe to The Cherry Fairy Chronicles';
  const newsletterText = settings.home_newsletter_text
    || 'New tales of whimsy and wonder, delivered straight to your inbox. Join our circle of readers and never miss a Chronicle.';

  const footerText = settings.home_footer_text || 'Make Families Happy';

  // One-shot gift toast.
  const [toastMessage, setToastMessage] = useState(null);
  useEffect(() => {
    try {
      const msg = sessionStorage.getItem('giftToast');
      if (msg) {
        setToastMessage(msg);
        sessionStorage.removeItem('giftToast');
      }
    } catch { /* sandboxed sessionStorage — ignore */ }
  }, []);

  // ─── Community tally (automatic) ──────────────────────
  // The endpoint returns every metric plus `value` (the number for the
  // admin-selected metric). We just display `value`.
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(false);
  useEffect(() => {
    let active = true;
    api.get('/public/stats/community', false)
      .then((data) => { if (active) setStats(data); })
      .catch(() => { if (active) setStatsError(true); });
    return () => { active = false; };
  }, []);

  // ─── Newsletter subscribe ─────────────────────────────
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeMsg, setSubscribeMsg] = useState(null);

  const handleSubscribe = async () => {
    setSubscribeMsg(null);
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setSubscribeMsg({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }
    setSubscribing(true);
    try {
      const data = await api.post('/public/newsletter/subscribe', {
        email: trimmed,
        firstname: firstName.trim() || undefined,
        lastname: lastName.trim() || undefined,
      }, false);
      setSubscribeMsg({ type: 'success', text: data.message || "You're subscribed!" });
      setEmail(''); setFirstName(''); setLastName('');
    } catch (err) {
      setSubscribeMsg({ type: 'error', text: err.data?.error || 'Something went wrong. Please try again.' });
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">

      {/* ═══ HERO — dark ═══ */}
      <section className="relative overflow-hidden bg-royal-900">
        <div className="absolute inset-0 bg-hero-gradient opacity-80"></div>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.08) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(244,208,63,0.05) 0%, transparent 50%)'
        }}></div>
        <div className="relative max-w-3xl mx-auto px-4 pt-12 sm:pt-16 md:pt-20 pb-24 sm:pb-32 text-center">
          <p className="font-body text-gold-light text-base sm:text-lg mb-3 sm:mb-4 italic">{heroWelcome}</p>
          <h1 className="font-display text-3xl sm:text-5xl md:text-6xl text-gold mb-3 sm:mb-4 leading-tight">
            {siteName}
          </h1>

          <div className="flex justify-center my-5 sm:my-6">
            <img
              src="https://storage.thecherryfairyshop.com/fairy%20ball%20nymph%20resized.jpg"
              alt="Fairy ball nymph watercolor"
              loading="lazy"
              className="w-72 sm:w-96 h-auto rounded-2xl ring-2 ring-gold/40 shadow-[0_0_40px_rgba(212,175,55,0.25)]"
            />
          </div>

          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-5 sm:mb-7">
            <div className="h-px w-10 sm:w-16 bg-gradient-to-r from-transparent via-gold to-transparent"></div>
            <span className="text-gold text-base sm:text-lg">✦</span>
            <div className="h-px w-10 sm:w-16 bg-gradient-to-r from-transparent via-gold to-transparent"></div>
          </div>

          <div className="flex gap-3 sm:gap-4 justify-center flex-col sm:flex-row px-4 sm:px-0">
            <Link
              to="/moonbeams"
              className="px-6 sm:px-8 py-3 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors text-center"
            >
              {heroBrowseBtn}
            </Link>
            <Link
              to="/stories"
              className="px-6 sm:px-8 py-3 border border-gold text-gold font-semibold rounded-lg text-base hover:bg-gold/10 transition-colors text-center"
            >
              {heroStoriesBtn}
            </Link>
          </div>
        </div>

        <div className="absolute -bottom-px left-0 right-0 pointer-events-none">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none">
            <path
              d="M0 80L60 72C120 64 240 48 360 40C480 32 600 32 720 36C840 40 960 48 1080 52C1200 56 1320 56 1380 56L1440 56V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z"
              className="fill-cream dark:fill-bg-dark"
            />
          </svg>
        </div>
      </section>

      {/* ═══ MOONBEAMS SHARED — tally — cream ═══ */}
      <section className="max-w-4xl mx-auto px-4 py-12 sm:py-20">
        <h2 className="font-display text-xl sm:text-2xl text-gold-dark dark:text-gold text-center mb-2">
          {tallyHeading}
        </h2>
        <div className="flex items-center justify-center gap-3 mb-8 sm:mb-12">
          <div className="w-1.5 h-1.5 rounded-full bg-gold-dark dark:bg-gold"></div>
          <div className="h-px w-12 bg-gold-dark dark:bg-gold"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-gold-dark dark:bg-gold"></div>
        </div>

        {/* Tally box — gold border all around, ✦ in all four corners,
            number only (no kindness/deliveries sub-lines). */}
        <div className="relative bg-white dark:bg-royal-800/40 border-2 border-gold/60 rounded-2xl p-8 sm:p-12 text-center overflow-hidden">
          <CornerSparkles />

          {statsError ? (
            <p className="text-royal-500 dark:text-royal-200 text-base italic">
              {cardsName} are lighting up inboxes and hearts everywhere.
            </p>
          ) : (
            <>
              <p className="font-display text-5xl sm:text-7xl text-gold-dark dark:text-gold leading-none mb-3">
                {stats ? <CountUp value={stats.value} /> : '—'}
              </p>
              <p className="text-royal-600 dark:text-royal-100 text-base sm:text-lg italic">
                {tallyLabel}
              </p>
            </>
          )}
        </div>

        {/* Admin-editable community paragraph */}
        {communityText && (
          <p className="text-center text-royal-600 dark:text-royal-100 text-base sm:text-lg italic leading-relaxed max-w-2xl mx-auto mt-8 sm:mt-10 px-2">
            {communityText}
          </p>
        )}
      </section>

      {/* ═══ FREE FAIRY TALES — stories — dark ═══ */}
      <section className="relative overflow-hidden py-12 sm:py-20">
        <div className="absolute inset-0 bg-royal-900"></div>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 20% 30%, rgba(212,175,55,0.1) 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(244,208,63,0.06) 0%, transparent 50%)'
        }}></div>
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-display text-xl sm:text-2xl text-gold mb-2">{storiesHeading}</h2>
          <div className="flex items-center justify-center gap-3 mb-8 sm:mb-10">
            <div className="w-1.5 h-1.5 rounded-full bg-gold-light"></div>
            <div className="h-px w-12 bg-gold-light"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-gold-light"></div>
          </div>

          <p className="text-royal-100 text-base sm:text-lg italic leading-relaxed max-w-xl mx-auto mb-8 sm:mb-10 px-2">
            {storiesIntro}
          </p>

          <div className="grid grid-cols-2 gap-3 sm:gap-5 max-w-sm sm:max-w-lg mx-auto">
            <Link
              to="/stories?category=1"
              className="bg-royal-800/40 border border-royal-600 rounded-xl p-4 sm:p-6 text-center hover:-translate-y-1 hover:shadow-lg hover:shadow-gold/5 transition-all group"
            >
              <span className="text-2xl sm:text-3xl block mb-2 sm:mb-3">📖</span>
              <span className="font-display text-sm sm:text-base text-gold group-hover:text-gold-light transition-colors">{storiesBtn1}</span>
            </Link>
            <Link
              to="/stories?category=2"
              className="bg-royal-800/40 border border-royal-600 rounded-xl p-4 sm:p-6 text-center hover:-translate-y-1 hover:shadow-lg hover:shadow-gold/5 transition-all group"
            >
              <span className="text-2xl sm:text-3xl block mb-2 sm:mb-3">🍒</span>
              <span className="font-display text-sm sm:text-base text-gold group-hover:text-gold-light transition-colors">{storiesBtn2}</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ SUBSCRIBE — newsletter — cream ═══ */}
      <section className="max-w-3xl mx-auto px-4 py-12 sm:py-20 text-center">
        <div className="flex items-center justify-center gap-3 mb-8 sm:mb-10">
          <div className="w-1.5 h-1.5 rounded-full bg-gold-dark dark:bg-gold"></div>
          <div className="h-px w-12 sm:w-20 bg-gold-dark dark:bg-gold"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-gold-dark dark:bg-gold"></div>
          <div className="h-px w-12 sm:w-20 bg-gold-dark dark:bg-gold"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-gold-dark dark:bg-gold"></div>
        </div>

        <p className="font-display text-2xl sm:text-3xl text-gold-dark dark:text-gold mb-4 sm:mb-5">
          {newsletterHeading}
        </p>
        <p className="text-royal-500 dark:text-royal-100 text-base sm:text-lg italic leading-relaxed max-w-lg mx-auto mb-8 sm:mb-10 px-2">
          {newsletterText}
        </p>

        <div className="max-w-md mx-auto">
          <div className="flex flex-col sm:flex-row gap-3 mb-3">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name (optional)"
              className="flex-1 px-4 py-3 bg-white dark:bg-royal-800/40 border border-royal-200 dark:border-royal-600 rounded-lg text-royal-900 dark:text-white text-base placeholder-royal-400 dark:placeholder-royal-300 focus:outline-none focus:border-gold/60 transition-colors"
            />
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name (optional)"
              className="flex-1 px-4 py-3 bg-white dark:bg-royal-800/40 border border-royal-200 dark:border-royal-600 rounded-lg text-royal-900 dark:text-white text-base placeholder-royal-400 dark:placeholder-royal-300 focus:outline-none focus:border-gold/60 transition-colors"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubscribe()}
              placeholder="your@email.com"
              className="flex-1 px-4 py-3 bg-white dark:bg-royal-800/40 border border-royal-200 dark:border-royal-600 rounded-lg text-royal-900 dark:text-white text-base placeholder-royal-400 dark:placeholder-royal-300 focus:outline-none focus:border-gold/60 transition-colors"
            />
            <button
              onClick={handleSubscribe}
              disabled={subscribing}
              className="px-6 sm:px-8 py-3 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {subscribing ? 'Subscribing…' : 'Subscribe'}
            </button>
          </div>

          {subscribeMsg && (
            <p className={`mt-3 text-sm ${
              subscribeMsg.type === 'success'
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-500 dark:text-red-400'
            }`}>
              {subscribeMsg.text}
            </p>
          )}

          <p className="text-royal-400 dark:text-royal-300 text-xs mt-3 italic">
            No spam, ever. Unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* Footer divider */}
      <div className="border-t border-royal-200/30 dark:border-royal-700 py-4 sm:py-6 text-center px-4">
        <p className="text-royal-500 dark:text-royal-200 italic text-sm sm:text-base">
          ✦ &nbsp; {footerText} &nbsp; ✦
        </p>
      </div>

      <Toast message={toastMessage} onDone={() => setToastMessage(null)} />
    </div>
  );
}